// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import { ApolloError } from '@apollo/client';

import { useCustomerConfig, useUser } from '@store';
import { Moves } from '@gql/base-types';
import { generateMovesCSV } from '@services/csvService';

import { css, keyframes } from '@emotion/css';
import { useTheme, Theme, Chip, LinearProgress, Tab, Tabs, Typography } from '@mui/material';
import { BiFile } from 'react-icons/bi';

import AssignRefModal from '@components/Modals/AssignRefModal';

import DefaultFallback from '@components/Fallbacks/DefaultFallback';
import Spacer from '@components/Utils/Spacer';
import Toolbar from '@components/Utils/Toolbar';

import { ReportView, useMissingRefReport } from '@features/missingRefReport/providers/MissingRefReportProvider';

import MissingRefReportControls from '@features/missingRefReport/components/MissingRefReportControls';
import MissingRefReportMetrics from '@features/missingRefReport/components/MissingRefReportMetrics';
import MissingRefReportTable from '@features/missingRefReport/components/MissingRefReportTable';
import MissingRefReportPagination from './MissingRefReportPagination';
import MissingRoServiceStopsSection, {
  MissingRoStop,
} from '@features/missingRefReport/components/MissingRoServiceStopsSection';

// TYPES ---------------------------------------------------------------- //

interface MissingRefReportContentProps {
  loading: boolean;
  error: ApolloError | undefined;
  refetch: () => Promise<void>;
  moves: Moves[];
  aggregateCounts: {
    allMoves: number;
    upcomingMoves: number;
    inProgressMoves: number;
    completedMoves: number;
    canceledMoves: number;
  };
  usernamesPool: string[];
  serviceStops: MissingRoStop[];
  serviceStopsCount: number;
  serviceStopsLoading: boolean;
  showServiceStops: boolean;
}

// COMPONENT ---------------------------------------------------------------- //

const MissingRefReportContent = ({
  loading,
  error,
  refetch,
  moves,
  aggregateCounts,
  usernamesPool,
  serviceStops,
  serviceStopsCount,
  serviceStopsLoading,
  showServiceStops,
}: MissingRefReportContentProps) => {
  const theme = useTheme();
  const cls = useStyles(theme);

  const user = useUser();
  const customerConfig = useCustomerConfig();
  const refLabel = customerConfig?.reference_num_label || 'Reference ID';
  const missingRefLabel = customerConfig?.reference_num_report_label || 'Missing Ref Report';
  const isAdmin =
    user?.profile?.default_role === 'admin' ||
    user?.profile?.default_role === 'dealer-super-admin' ||
    user?.profile?.default_role === 'dealer-admin' ||
    user?.profile?.default_role === 'program-admin';

  const {
    disablePickers,
    end,
    limit,
    metric,
    offset,
    onClearFilters,
    onDateChange,
    onMetricChange,
    onRangeChange,
    onViewChange,
    range,
    search,
    setLimit,
    setOffset,
    setSearch,
    setUsernames,
    start,
    usernames,
    view,
    lockedView,
  } = useMissingRefReport();

  // Force the moves view when the customer doesn't have mobile service enabled
  const activeView: ReportView = showServiceStops ? view : 'moves';
  const activeLoading = activeView === 'mobileservice' ? serviceStopsLoading : loading;
  // The internal switch only shows when the customer has both views and the host hasn't locked one.
  const showViewSwitch = showServiceStops && !lockedView;

  const [loadingVisible, setLoadingVisible] = React.useState<boolean>(false);
  React.useEffect(() => {
    if (activeLoading) {
      setLoadingVisible(true);
    } else {
      setTimeout(() => setLoadingVisible(false), 250);
    }
  }, [activeLoading]);

  const [assignRefModal, setAssignRefModal] = React.useState<{ open: boolean; input: any; output: any }>({
    open: false,
    input: null,
    output: null,
  });
  const handleAssignRefModalOpen = (input?: any) => {
    setAssignRefModal({ ...assignRefModal, open: true, input });
  };
  const handleAssignRefModalClose = (output?: any) => {
    setAssignRefModal({ ...assignRefModal, open: false, output });
    if (output) refetch();
  };

  // Get the total count for the current metric (for pagination)
  const moveCount = React.useMemo(() => {
    if (metric === 'all') return aggregateCounts.allMoves;
    if (metric === 'upcoming') return aggregateCounts.upcomingMoves;
    if (metric === 'inprogress') return aggregateCounts.inProgressMoves;
    if (metric === 'completed') return aggregateCounts.completedMoves;
    if (metric === 'canceled') return aggregateCounts.canceledMoves;
    return aggregateCounts.allMoves;
  }, [metric, aggregateCounts]);

  // Filter moves based on current filters
  // Note: Metric and search filtering are now done server-side via GraphQL query
  const filteredMoves = React.useMemo(() => {
    let filtered = [...moves];

    // Apply usernames filter (client-side)
    if (usernames.length > 0) {
      filtered = filtered.filter(move => usernames.includes(move?.createdBy || ''));
    }

    return filtered;
  }, [moves, usernames]);

  return (
    <>
      <AssignRefModal
        open={assignRefModal?.open}
        onClose={handleAssignRefModalClose}
        moveInput={assignRefModal?.input?.move}
      />

      <div className={cls.header}>
        <Toolbar
          fullscreen
          zIndex={0}
          position='relative'
          title={missingRefLabel}
          refetch={refetch}
          onClear={onClearFilters}
          actions={[
            {
              label: 'Export CSV',
              icon: <BiFile style={{ marginRight: 8, color: theme?.palette?.text?.primary }} />,
              handler: () => generateMovesCSV(filteredMoves, start, end, isAdmin),
              disabled: activeView === 'mobileservice' || filteredMoves.length === 0,
            },
          ]}
        />

        <Typography sx={{ ml: 2, fontSize: 14, color: theme?.palette?.text?.secondary }}>
          {activeView === 'mobileservice'
            ? `This is a report for mobile service stops that are missing their RO #. Fix them by clicking the assign button on their row and updating the RO # on the linked appointment.`
            : `This is a report for moves that are missing their ${refLabel}. Fix them by clicking the assign button on their row and updating their ${refLabel}.`}
        </Typography>

        {showViewSwitch ? (
          <>
            <Spacer size={1} />

            <Tabs
              value={activeView}
              onChange={(_: React.SyntheticEvent, newView: ReportView) => onViewChange(newView)}
              sx={{ px: 2 }}
              aria-label='Missing ref report views'
            >
              <Tab
                value='moves'
                label={
                  <span className={cls.tabLabel}>
                    Moves
                    {aggregateCounts.allMoves > 0 ? (
                      <Chip size='small' label={aggregateCounts.allMoves} color='error' />
                    ) : null}
                  </span>
                }
              />
              <Tab
                value='mobileservice'
                label={
                  <span className={cls.tabLabel}>
                    Mobile Service
                    {serviceStopsCount > 0 ? <Chip size='small' label={serviceStopsCount} color='error' /> : null}
                  </span>
                }
              />
            </Tabs>

            <Spacer size={2} />
          </>
        ) : (
          <Spacer size={3} />
        )}

        <div className={cls.headerDetails}>
          <MissingRefReportControls
            range={range}
            start={start}
            end={end}
            disablePickers={disablePickers}
            usernames={usernames}
            search={search}
            setUsernames={setUsernames}
            setSearch={setSearch}
            onRangeChange={onRangeChange}
            onDateChange={onDateChange}
            usernamesPool={usernamesPool}
          />

          {activeView === 'moves' ? (
            <>
              <Spacer size={5} />

              <MissingRefReportMetrics
                aggregateCounts={aggregateCounts}
                metric={metric}
                onMetricChange={onMetricChange}
              />
            </>
          ) : null}
        </div>
      </div>

      {loadingVisible ? (
        <LinearProgress className={activeLoading ? cls.fadeIn : cls.fadeOut} />
      ) : (
        <div className={cls.loadingPad} />
      )}

      {error ? (
        <>
          <Spacer size={2} />
          <DefaultFallback fullscreen severity='error' message='Failed to fetch moves.' />
        </>
      ) : null}

      {activeView === 'moves' ? (
        <>
          {!loading && !error && !filteredMoves.length ? (
            <>
              <Spacer size={2} />
              <DefaultFallback
                fullscreen
                severity='warning'
                message='No moves found. Try adjusting your date range, metric or other filters...'
              />
            </>
          ) : null}

          {!error && filteredMoves.length > 0 ? (
            <>
              {/* TOP PAGINATION */}
              <MissingRefReportPagination
                moveCount={moveCount}
                offset={offset}
                limit={limit}
                setOffset={setOffset}
                setLimit={setLimit}
              />

              {/* CONTENT */}
              <div className={loading ? cls.contentLoading : cls.content}>
                <MissingRefReportTable
                  moves={filteredMoves}
                  moveCount={moveCount}
                  metric={metric}
                  isAdmin={isAdmin}
                  payersArray={[]}
                  onAssignRef={handleAssignRefModalOpen}
                />

                {/* BOTTOM PAGINATION - Inside of content div to avoid layout shift */}
                <MissingRefReportPagination
                  moveCount={moveCount}
                  offset={offset}
                  limit={limit}
                  setOffset={setOffset}
                  setLimit={setLimit}
                />
              </div>
            </>
          ) : null}
        </>
      ) : (
        /* MOBILE SERVICE STOPS MISSING RO # */
        <div className={serviceStopsLoading ? cls.contentLoading : cls.content}>
          <MissingRoServiceStopsSection stops={serviceStops} search={search} refetch={refetch} />
        </div>
      )}
    </>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme: Theme) => {
  const fadeIn = keyframes`
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  `;

  const fadeOut = keyframes`
    0% {
      opacity: 1;
    }
    100% {
      opacity: 0;
    }
  `;

  const contentFadeIn = keyframes`
    0% {
      opacity: 0;
    }
    33% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  `;

  return {
    header: css`
      background-color: ${theme.palette.background.paper};
      border-bottom: 1px solid ${theme.palette.divider};
    `,
    headerDetails: css`
      padding: ${theme.spacing(0, 2, 2, 2)};
    `,
    tabLabel: css`
      display: flex;
      align-items: center;
      gap: 8px;
    `,

    fadeIn: css`
      opacity: 1;
      animation: ${fadeIn} 0.25s ease-in-out;
    `,
    fadeOut: css`
      opacity: 0;
      animation: ${fadeOut} 0.25s ease-in-out;
    `,
    loadingPad: css`
      width: 100%;
      height: 4px;
    `,

    content: css`
      opacity: 1;
      transition: opacity 0.25s ease-in-out;
      animation: ${contentFadeIn} 0.5s ease-in-out;
    `,
    contentLoading: css`
      opacity: 0;
      transition: opacity 0.25s ease-in-out;
      user-select: none;
      pointer-events: none;
    `,
  };
};

// EXPORT ---------------------------------------------------------------- //

export default MissingRefReportContent;
